import type { Config } from "./config";
import { setupMessageMediaThemes } from "./portals/messagemedia/theme";

import { traceManager, type SpanEntry } from "./sentry";
const getGlobalJotaiStore = async () =>
  (await import(/* webpackMode: "eager" */ "./jotai-store"))?.globalJotaiStore;

async function setupExceptionMonitoring() {
  const { initExceptionMonitoring } = await import("./exceptionMonitoring");
  const { isBrowserLackingCriticalFunctionality, isBrowserOld } = await import(
    "./browserSupport"
  );

  const missingCriticalBrowserSupport = isBrowserLackingCriticalFunctionality();
  const missingBrowserVersionSupport = isBrowserOld();
  initExceptionMonitoring({
    missingCriticalBrowserSupport,
    missingBrowserVersionSupport,
  });
}

const removeCacheBustingParam = () => {
  const params = new URLSearchParams(location.search);
  if (params.has("t")) {
    params.delete("t");
    window.history.replaceState(
      {},
      "",
      `${location.pathname}${params.size > 0 ? `?${params.toString()}` : ""}`
    );
  }
};

type Step = (cfg: Config) => Promise<void>;

function runSteps(steps: Step[], cfg: Config): Promise<void> {
  return Promise.all(steps.map((step) => step(cfg))).then(() => {
    void 0;
  });
}

/**
 * A tranche is a series of steps that run in parallel.
 */
type Tranche = [SpanEntry, Step[]];

/**
 * Runs a series of tranches, where each tranche runs in series, but the contents of each tranche run in parallel.
 * @param tranches the tranches, which finish one after the other, the contents of each tranche run in parallel
 * @param opts
 */
async function runTranches(tranches: Tranche[], cfg: Config): Promise<void> {
  for (const [name, tranche] of tranches) {
    const trace = traceManager.start(name, {
      parentSpan: traceManager.getTransaction("UniversalBootstrap") ?? null,
    });
    await runSteps(tranche, cfg);
    trace.end();
  }
}

function conditionalStep(
  criteria: (cfg: Config) => boolean,
  step: Step,
  alternativeStep?: Step
): Step {
  return async (cfg) => {
    if (criteria(cfg)) {
      await step(cfg);
    } else if (alternativeStep) {
      await alternativeStep(cfg);
    }
  };
}

/**
 * Turns a step into a background task that doesn't need to terminate for rendering to progress.
 */
function backgroundStep(s: Step): Step {
  return async (opts) => {
    setTimeout(() => {
      s(opts);
    }, 0);
  };
}

const setupFeatureFlags = async () => {
  const { CONFIGCAT_SDK_KEY: featureFlagClientKey } = window.SHELL_ENV;

  const { assignFeatureFlags, initFeatureFlags } = await import(
    /* webpackMode: "eager" */
    "./config/feature-flags/config-cat-client"
  );
  const globalJotaiStore = await getGlobalJotaiStore();

  const { featureFlagContractAtom } = await import(
    /* webpackMode: "eager" */ "./featureFlags"
  );
  await initFeatureFlags({
    featureFlagClientKey,
  });
  const sessionContract = globalJotaiStore.get(featureFlagContractAtom);
  await assignFeatureFlags(sessionContract);
};

const setupFeatureFlagsStep: Step = async () => {
  await setupFeatureFlags();
};

const requireCriticalBrowserSupport = conditionalStep(
  ({ universalBootstrapOptions: { requireCriticalBrowserSupport } }) =>
    requireCriticalBrowserSupport,
  async () => {
    const { isBrowserLackingCriticalFunctionality } = await import(
      "./browserSupport"
    );

    if (isBrowserLackingCriticalFunctionality()) {
      history.pushState("", "", "/missing-browser-support");
    }
  }
);

const setFaviconAndTitleStep: Step = async () => {
  const { setFaviconAndTitle } = await import(
    /* webpackMode: "eager" */ "./setFaviconAndTitle"
  );
  setFaviconAndTitle();
};

const setCountryStep: Step = async () => {
  const { setCountryConfig } = await import(
    /* webpackMode: "eager" */ "./setCountryConfig"
  );

  await setCountryConfig();
};

const busOverrideFFUserListenerStep: Step = async () => {
  import(/* webpackMode: "eager" */ "./bus-override-ff-user-listener");
};
const busClearStorageListenerStep: Step = async () => {
  import(/* webpackMode: "eager" */ "./busClearStorage");
};
const busCrashListenerStep: Step = async () => {
  import(/* webpackMode: "eager" */ "./bus-crash-listener");
};
const busPlatformHandlerStep: Step = async () => {
  import(/* webpackMode: "eager" */ "./bus-platform-handler");
};

const busTokenHandlerStep: Step = async () => {
  import(/* webpackMode: "eager" */ "./bus-token-handler");
};
const evaluateRegionStep: Step = async () => {
  import(/* webpackMode: "eager" */ "./evaluateRegion");
};

const globalJotaiStoreStep: Step = async () => {
  const globalJotaiStore = await getGlobalJotaiStore();
  const { syncLocation } = await import(
    /* webpackMode: "eager" */ "./components/Router/state"
  );
  syncLocation(globalJotaiStore);
};

const mfeLoaderStep: Step = async () => {
  const { setupMFELoaders } = await import(
    /* webpackMode: "eager" */ "./mfe/mfeLoaders"
  );
  const { ENV } = await import(
    /* webpackMode: "eager" */ "./config/loadShellEnv"
  );

  setupMFELoaders(ENV);
  import(/* webpackPreload: true */ "./mfe/bus-mfe-config-handler");
};

const messageMediaThemesStep = conditionalStep(
  ({ universalBootstrapOptions: { messageMediaThemes } }) => messageMediaThemes,
  async () => {
    setupMessageMediaThemes();
  }
);

const cacheLayerStep = async () => {
  const { patchFetchWithCacheLayer } = await import(
    /* webpackMode: "eager" */ "./patchFetchWithCache"
  );
  patchFetchWithCacheLayer();
};

const patchFetchBusLayerStep = async () => {
  const { patchFetchWithBus } = await import(
    /* webpackMode: "eager" */ "./patchFetchWithBus"
  );
  patchFetchWithBus();
};

const busReplayBrokerStep = async () => {
  const { busReplayBroker } = await import(
    /* webpackMode: "eager" */ "./busReplayBroker"
  );
  busReplayBroker();
};

const projectIdHeaderStep = conditionalStep(
  ({ universalBootstrapOptions: { projectIdHeader } }) => projectIdHeader,
  async () => {
    const { enableProjectIdHeader } = await import("./x-project-id");
    enableProjectIdHeader();
  }
);

const googleTagManagerStep = conditionalStep(
  ({ universalBootstrapOptions: { googleTagManager } }) => googleTagManager,
  async () => {
    const { initGoogleTagManager } = await import(
      "./analytics/googleTagManager"
    );
    const { setDefaultGtagConsent } = await import(
      "./components/CookieBanner/gtag-consent"
    );
    initGoogleTagManager();
    setDefaultGtagConsent();
  }
);

const hotjarStep = conditionalStep(
  ({ universalBootstrapOptions: { hotjarId } }) => !!hotjarId,
  async ({ universalBootstrapOptions: { hotjarId } }) => {
    if (!hotjarId) return;
    const { loadHotjar } = await import("./analytics/hotjar");
    loadHotjar(hotjarId);
  }
);

const rudderstackStep = backgroundStep(
  conditionalStep(
    ({ universalBootstrapOptions: { rudderstack } }) => rudderstack !== null,
    async ({ universalBootstrapOptions: { rudderstack } }) => {
      if (!rudderstack) return;
      const { initRudderstack } = await import("./analytics/rudderstack");

      initRudderstack(
        rudderstack.startSession ?? false,
        rudderstack.dataPlaneUrl,
        rudderstack.writeKey
      );
    }
  )
);

const exceptionMonitoringStep = backgroundStep(
  conditionalStep(
    ({ universalBootstrapOptions: { exceptionMonitoring } }) =>
      exceptionMonitoring,
    async () => {
      await setupExceptionMonitoring();
    }
  )
);

const setDefaultLanguageToBrowserDefaultStep = conditionalStep(
  ({ universalBootstrapOptions: { setDefaultLanguageToBrowserDefault } }) =>
    setDefaultLanguageToBrowserDefault,
  async () => {
    const { setWindowIntlMeta, getBrowserLang } = await import(
      "./intl/intl.utils"
    );
    setWindowIntlMeta(getBrowserLang());
  },
  async () => {
    // Initialize intl object
    window.meta = {
      ...window.meta,
      intl: {
        locale: "",
        language: "",
        country: "",
      },
    };
  }
);

const addWindowValues: Step = async (config) => {
  const { extraWindowValues } = config.universalBootstrapOptions;
  if (extraWindowValues) {
    extraWindowValues.forEach((kv) => {
      Object.assign(window, { [kv.key]: kv.value });
    });
  }
};

const configToGlobal: Step = async (config) => {
  const { setPortalConfig } = await import(
    /* webpackMode: "eager" */ "./config/portalConfig"
  );
  setPortalConfig("configName", config.SHELL_ENV.CONFIG_NAME);
};

const patchHistoryStep: Step = async () => {
  const { patchHistory } = await import(
    /* webpackMode: "eager" */ "./patchHistory"
  );
  patchHistory();
};

const registerSentryMetricsStep: Step = async () => {
  const { trackLocalStorage } = await import(
    /* webpackMode: "eager" */ "./sentry"
  );
  trackLocalStorage();
};

declare global {
  interface Window {
    __REACT_INTL_BYPASS_GLOBAL_CONTEXT__: boolean;
  }
}

const fixReactIntl = async () => {
  /* This is a workaround to bypass the global context check in react-intl and create isssues when mfes uses different react versions.*/
  window.__REACT_INTL_BYPASS_GLOBAL_CONTEXT__ = true;
};

const CONFIG_TRANCH = [setupFeatureFlagsStep, configToGlobal, fixReactIntl];

const EXCEPTION_MONITORING_TRANCH = [exceptionMonitoringStep];

// other steps that need to run before the main bootstrap steps
const PREREQUISITE_TRANCH = [
  async () => removeCacheBustingParam(),
  patchHistoryStep,
  cacheLayerStep,
  messageMediaThemesStep,
  setDefaultLanguageToBrowserDefaultStep,
  // Some GTM tags might rely on rudderstack being loaded first
  setCountryStep,
  rudderstackStep,
];

const UNIVERSAL_BOOTSTRAP_STEPS: Step[] = [
  requireCriticalBrowserSupport,
  setFaviconAndTitleStep,
  busPlatformHandlerStep,
  busTokenHandlerStep,
  busCrashListenerStep,
  busOverrideFFUserListenerStep,
  busClearStorageListenerStep,
  globalJotaiStoreStep,
  mfeLoaderStep,
  projectIdHeaderStep,
  googleTagManagerStep,
  hotjarStep,
  addWindowValues,
  evaluateRegionStep,
  patchFetchBusLayerStep,
  busReplayBrokerStep,
  registerSentryMetricsStep,
];

const BOOTSTRAP_TRANCHES: Array<Tranche> = [
  ["config-tranche", CONFIG_TRANCH],
  ["exception-monitoring-tranche", EXCEPTION_MONITORING_TRANCH],
  ["prerequisites-tranche", PREREQUISITE_TRANCH],
  ["universal-bootstrap-steps", UNIVERSAL_BOOTSTRAP_STEPS],
];

export async function universalBootstrap(config: Config): Promise<void> {
  await runTranches(BOOTSTRAP_TRANCHES, config);
}
